import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import type {
  ForkOfferedPayload,
  ForkOriginPayload,
  HostChangedPayload,
  HostingChangedPayload,
  OutcomeNoticePayload,
  ProblemPayload,
  SettingsChangedPayload,
  ThreadBindings,
  ThreadEntry,
  ThreadStartedPayload,
  WorkflowEventPayload,
} from "../../../api/threads/liveMessages";
import { acceptForkOffer } from "../../../api/threads/threadHostingApi";
import { newClientKey } from "../../../api/threads/threadsApi";
import { appPaths } from "../../../shared/routing/appPaths";
import ErrorNotice from "../../../errors/ErrorNotice";
import { parseThreadEntryRef } from "../../../shared/references/threadReferences";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import CopyReference from "../../../ui/content/CopyReference";
import DefinitionListItem from "../../../ui/layout/DefinitionListItem";
import { notifyThreadListChanged } from "../data/threadListEvents";
import BoundVersions from "../../versions/labels/BoundVersions";
import { workflowEventKey } from "./workflowEventWords";
import { useThreadContext } from "../page/threadPageContext";
import { describeUiError } from "../../../errors/errorDescriptions";
import RouteNotReady from "./RouteNotReady";
import { routeNotReadyOf } from "./routeReadiness";
import ChooseAgent from "../hosting/ChooseAgent";
import { recordedProblemError } from "./recordedProblems";
import { routeLabel } from "./routeLabels";

/** Entries that record what happened to the Thread rather than what was said in it. */

export function ThreadStartedBody({ payload }: { payload: ThreadStartedPayload }) {
  const { t } = useTranslation();
  const { overview, workflowName, workflowNameOf } = useThreadContext();
  // A Workflow by its name, never its version reference (P-F): the Thread's own, or another,
  // such as one left by a switch (F126), among the content version's Workflows.
  const name = payload.workflow === null ? null : ((payload.workflow === overview.workflow ? workflowName : null) ?? workflowNameOf(payload.workflow));
  const workflow = name === null ? t("threads.events.withSomeWorkflow") : t("threads.events.withWorkflow", { workflow: name });
  return (
    <>
      {t("threads.events.started", { title: payload.title })} {payload.workflow === null ? t("threads.events.free") : workflow}
    </>
  );
}

export function ForkOriginBody({ payload }: { payload: ForkOriginPayload }) {
  const { t } = useTranslation();
  const { nameOf } = useThreadContext();
  const parent = parseThreadEntryRef(payload.parent);
  return (
    <>
      {t("threads.events.forkedBy", { user: nameOf(payload.by) })}{" "}
      {parent === null ? (
        payload.parent
      ) : (
        <Link className="app-link app-link-cue-away" to={appPaths.thread(parent.threadId, parent.number)} title={payload.parent}>
          {t("threads.header.forkOfEntry", { number: parent.number })}
        </Link>
      )}
      <CopyReference value={payload.parent} className="ml-1 inline-flex align-middle" />
      {payload.offer_entry === null ? null : ` ${t("threads.events.fromOffer", { entry: payload.offer_entry })}`}
      {payload.continue_with_step === null ? null : ` ${t("threads.events.continuesWith", { step: payload.continue_with_step })}`}
    </>
  );
}

export function SettingsChangedBody({ payload }: { payload: SettingsChangedPayload }) {
  const { t } = useTranslation();
  const route = payload.settings.route;
  return (
    <>
      {t("threads.events.settingsChanged", {
        route: route === null ? t("threads.events.noRoute") : routeLabel(route, t),
        profile: payload.settings.permission_profile,
      })}
      <BindingsDetails bindings={payload.bindings} workflow={null} />
    </>
  );
}

/** A Workflow event in one line; `byRunner`: the runner wrote it, as a skip on the way to a run's step (PC-4). */
export function WorkflowEventBody({ payload, byRunner }: { payload: WorkflowEventPayload; byRunner: boolean }) {
  const { t } = useTranslation();
  const { stepName, stepNameIn, workflowNameOf } = useThreadContext();
  // A Workflow by its name, never its version reference (P-F).
  const named = (workflow: string | null | undefined) => (workflow === null || workflow === undefined ? "" : (workflowNameOf(workflow) ?? t("threads.switch.someWorkflow")));
  const left = payload.from_workflow ?? null;
  // A switch (F126) names the step of the Workflow left, by that Workflow's names.
  const after = left === null || payload.after_step === null || payload.after_step === undefined ? null : stepNameIn(left, payload.after_step);
  const key = workflowEventKey(payload, byRunner);
  return (
    <>
      {t(`threads.events.workflow.${key === "switched" && after === null ? "switchedUnsettled" : key}`, {
        step: after ?? (payload.step_key === null ? "" : stepName(payload.step_key)),
        workflow: named(payload.workflow),
        from: named(left),
        to: named(payload.workflow),
      })}
    </>
  );
}

export function HostChangedBody({ payload }: { payload: HostChangedPayload }) {
  const { t } = useTranslation();
  const { nameOf } = useThreadContext();
  return <>{t("threads.events.hostChanged", { from: nameOf(payload.from_user), to: nameOf(payload.to_user) })}</>;
}

/** What became of a change set this Thread proposed or reviews (ADR-0026 slice 2c). */
export function OutcomeNoticeBody({ payload }: { payload: OutcomeNoticePayload }) {
  const { t } = useTranslation();
  const { nameOf } = useThreadContext();
  return <>{t(`threads.events.outcome.${payload.outcome}`, { by: nameOf(payload.by) })}</>;
}

export function HostingChangedBody({ payload }: { payload: HostingChangedPayload }) {
  const { t } = useTranslation();
  return <>{t("threads.events.hostingLine", { change: t(`threads.events.hosting.${payload.change}`), reason: t(`threads.events.hostingReasons.${payload.reason}`) })}</>;
}

/**
 * A problem in one line (F67: shorter): what went wrong and the server's own sentence for
 * this case. Without an agent chosen, its host chooses one right here.
 */
export function ProblemBody({ payload }: { payload: ProblemPayload }) {
  const { t } = useTranslation();
  const description = describeUiError(recordedProblemError(payload.problem));
  const missing = payload.problem.details["missing"];
  const needsRoute = Array.isArray(missing) && missing.includes("route");
  const notReady = routeNotReadyOf(payload.problem);
  return (
    <div className="flex flex-wrap items-center gap-x-2 text-sm">
      {notReady?.reason === "not_logged_in" ? null : <span className="app-status-text-error font-semibold">{t(description.titleKey, description.messageValues)}</span>}
      {notReady !== null ? (
        <RouteNotReady problem={notReady} />
      ) : (
        <span>{payload.problem.detail === "" ? t(description.messageKey, description.messageValues) : payload.problem.detail}</span>
      )}
      {needsRoute ? <ChooseAgent /> : null}
    </div>
  );
}

/** A fork offered to someone; the recipient forks there in one action (T-9). */
export function ForkOfferedBody({ entry, payload }: { entry: ThreadEntry; payload: ForkOfferedPayload }) {
  const { t } = useTranslation();
  const { nameOf, currentUserId, overview } = useThreadContext();
  const navigate = useNavigate();
  const [clientRequestId] = useState(newClientKey);
  const { state, submit } = useSubmission();
  const forMe = payload.to_user === currentUserId;

  const accept = () => {
    submit(async () => {
      const fork = await acceptForkOffer(entry.thread_id, entry.number, t("threads.fork.defaultTitle", { title: overview.title, entry: payload.at_entry }), clientRequestId);
      notifyThreadListChanged();
      void navigate(appPaths.thread(fork.threadId));
    });
  };

  return (
    <div className="space-y-2">
      <p>{t("threads.events.forkOffered", { user: nameOf(payload.to_user), entry: payload.at_entry })}</p>
      {forMe ? (
        <AppButton variant="primary" size="sm" disabled={state.status === "submitting"} onClick={accept}>
          {t("threads.events.acceptForkOffer")}
        </AppButton>
      ) : null}
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
    </div>
  );
}

/** The versions the Thread works on (threads.md T-3), by their labels; the exact references stay
 * copyable under Details (ADR-0033 D1, F29). */
function BindingsDetails({ bindings, workflow }: { bindings: ThreadBindings; workflow: string | null }) {
  const { t } = useTranslation();
  const { overview, workflowName } = useThreadContext();
  return (
    <details className="mt-0.5">
      <summary className="app-link cursor-pointer text-[11px]">{t("threads.turn.details")}</summary>
      <div className="mt-1 space-y-1">
        <BoundVersions
          projectId={overview.projectId}
          workflow={workflow === null ? null : { reference: workflow, name: workflow === overview.workflow ? workflowName : null }}
          contentVersion={bindings.content_version}
          corpusVersion={bindings.corpus_version}
        />
        {bindings.subject === null ? null : (
          <dl>
            <DefinitionListItem term={t("threads.versions.subject")} valueClassName="break-all font-mono">{bindings.subject}</DefinitionListItem>
          </dl>
        )}
      </div>
    </details>
  );
}
